// @flow
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { SingleSelect } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import useIntl from '../../../hooks/useIntl';
import messages from './messages';

export type MemberType = 'adult' | 'minor';

type Props = {
  open: boolean,
  onClose: () => void,
  onContinue: (memberType: MemberType) => void,
};

// ENG-30200 AC#1 -- "Add A Household Member" member-type chooser, shown
// between the Household section's Add button and the Add Adult/Minor
// Mini-Profile (MiniProfileBridge). Matches DESIGN-ENG-30200.md /
// design-ENG-30200-add-household-member-modal.png.
//
// Scope decisions (see DESIGN-ENG-30200.md "Decided scope"):
// - Exactly two options, Adult and Minor -- no Pets option (ENG-30200
//   excludes pets even though ENG-28929's copy mentions them).
// - No Start Date field -- ENG-28929 has one, ENG-30200's AC/design don't.
const AddHouseholdMemberModal = ({ open, onClose, onContinue }: Props) => {
  const intl = useIntl();
  const [memberType, setMemberType] = useState('');

  // AC#5: Cancel/close must create no record and leave no residual state --
  // reset here (keyed off `open`) so re-opening Add always starts from the
  // unselected/placeholder state, regardless of what was previously chosen.
  useEffect(() => {
    if (open) {
      setMemberType('');
    }
  }, [open]);

  const memberTypeOptions = [
    { value: 'adult', text: intl.formatMessage(messages.adultOption) },
    { value: 'minor', text: intl.formatMessage(messages.minorOption) },
  ];

  const handleContinue = () => {
    // Defensive only -- Continue is also disabled via the actionsProps
    // below whenever memberType is empty.
    if (!memberType) return;
    onContinue(memberType);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={intl.formatMessage(messages.title)}
      actionsProps={[
        {
          children: intl.formatMessage(messages.cancel),
          variant: 'text',
          onClick: onClose,
          'data-testid': 'add-household-member-modal-cancel-button',
        },
        {
          children: intl.formatMessage(messages.continue),
          variant: 'primary',
          onClick: handleContinue,
          // AC#1: Continue is disabled until a Member Type is selected.
          disabled: !memberType,
          'data-testid': 'add-household-member-modal-continue-button',
        },
      ]}
      ModalProps={{
        PaperProps: {
          sx: {
            width: { xs: 'calc(100% - 32px)', sm: 480 },
          },
        },
      }}
    >
      <Typography variant="bodyMedium" sx={{ mb: 2 }}>
        {intl.formatMessage(messages.instruction)}
      </Typography>
      <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
        <SingleSelect
          name="memberType"
          label={intl.formatMessage(messages.memberTypeLabel)}
          // The design mock's unselected state literally reads "Adult" in
          // placeholder gray (see messages.js comment + DESIGN-ENG-30200.md)
          // -- treated here as the literal placeholder copy. `value` stays
          // '' until the user actively picks an option from the dropdown,
          // so Continue remains disabled regardless of this placeholder text.
          placeholder={intl.formatMessage(messages.memberTypePlaceholder)}
          helperText={intl.formatMessage(messages.memberTypeRequired)}
          options={memberTypeOptions}
          value={memberType}
          onChange={(value) => setMemberType(value)}
          fullWidth
          // Phase 4 (Playwright) hook -- the underlying MUI Select's own
          // combobox is otherwise only addressable by its accessible name
          // ("Member Type"), which is fine for RTL's getByRole but a less
          // stable target for E2E.
          inputProps={{
            'data-testid': 'add-household-member-modal-member-type-select',
          }}
        />
      </Box>
    </Modal>
  );
};

export default AddHouseholdMemberModal;
